import React, { useEffect, useState, useRef } from 'react';
import {
  IconButton,
  Badge,
  Tooltip,
  Box,
  Fade,
  Zoom
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationPopover from './NotificationModal';
import { useLoginApi } from '../../Hooks/useLoginApi';
import { COLORS } from '../../theme/themeConstants';

// Use public folder path for audio file
const NOTIFICATION_SOUND_PATH = '/notification-sound.wav';

// Helper to read count from localStorage
const getInitialNotificationCount = (): number => {
  try {
    const stored = localStorage.getItem('userData');
    if (!stored) return 0;

    const parsed = JSON.parse(stored);
    const count = parsed?.userDetails?.notificationsCount || 0;
    return count;
  } catch (err) {
    console.error('Error parsing notificationsCount:', err);
    return 0;
  }
};

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState<number>(getInitialNotificationCount());
  const { getSpecialUser } = useLoginApi();
  
  const prevCountRef = useRef<number>(unreadCount);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    try {
      // Create audio element with proper source
      const audio = new Audio();
      audio.src = NOTIFICATION_SOUND_PATH;
      audio.volume = 1.0;
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      
      // Add error event listener
      audio.addEventListener('error', (e) => {
        console.error('Audio element error:', (e.target as HTMLAudioElement).error?.message);
      });
      
      audioRef.current = audio;
      
    } catch (err) {
      console.error('Failed to initialize audio element:', err);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);


  // Advanced notification sound function
  const playNotificationSound = async (): Promise<void> => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        
        console.log('Notification sound played successfully');
      }
    } catch (err) {
      console.error('Failed to play notification sound:', err);
      
      // Fallback: Create a new audio element and try again
      try {
        const audio = new Audio(NOTIFICATION_SOUND_PATH);
        audio.volume = 1.0;
        audio.crossOrigin = 'anonymous';
        
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        
        console.log('Notification sound played via fallback audio element');
      } catch (fallbackErr) {
        console.error('Fallback sound play also failed:', fallbackErr);
      }
    }
  };

  const handleBellClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
    setUnreadCount(0);
    // Update localStorage
    try {
      const stored = localStorage.getItem('userData');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.userDetails) {
          parsed.userDetails.notificationsCount = 0;
          localStorage.setItem('userData', JSON.stringify(parsed));
        }
      }
    } catch (error) {
      console.error('Failed to update notification count in localStorage:', error);
    }
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  // Polling for unread notification count every 20 seconds using getSpecialUser
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    const fetchCount = async (): Promise<void> => {
      try {
        const stored = localStorage.getItem('userData');
        if (!stored) return;
        const parsed = JSON.parse(stored);
        const userId = parsed?.userId;
        const userType = parsed?.userType;
        if (!userId || !userType) return;
        
        const res = await getSpecialUser({ userId, userType });
        type SpecialUserDetails = { notificationsCount?: number; unreadNotificationsCount?: number };
        const details = (res ?? {}) as SpecialUserDetails;
        const count =
          typeof details.notificationsCount === 'number'
            ? details.notificationsCount
            : typeof details.unreadNotificationsCount === 'number'
            ? details.unreadNotificationsCount
            : 0;
        
        // Play sound if count increased
        if (count > prevCountRef.current && prevCountRef.current >= 0) {
          await playNotificationSound();
        }
        
        prevCountRef.current = count;
        setUnreadCount(count);
      } catch (err) {
        console.error('Error fetching notification count:', err);
      }
    };
    
    fetchCount(); // Initial fetch
    interval = setInterval(fetchCount, 20000); 
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [getSpecialUser]);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={handleBellClick}
          sx={{
            position: 'relative',
            width: '37px',
            height: '37px',
            borderRadius: '50%',
            backgroundColor: COLORS.primary.main,
            padding: '8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: COLORS.primary.main,
              transform: 'scale(1.05)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
        >
          <Fade in={true} timeout={300}>
            <Box sx={{ position: 'relative' }}>
              <NotificationsIcon
                sx={{
                  color: COLORS.secondary.main,
                  fontSize: '24px'
                }}
              />
              {unreadCount > 0 && (
                <Zoom in={true} timeout={200}>
                  <Badge
                    badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      '& .MuiBadge-badge': {
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
                          },
                        },
                      }
                    }}
                  />
                </Zoom>
              )}
            </Box>
          </Fade>
        </IconButton>
      </Tooltip>
      {Boolean(anchorEl) && (
        <NotificationPopover
          anchorEl={anchorEl}
          open={true}
          onClose={handlePopoverClose}
        />
      )}
    </>
  );
};

export default NotificationBell;
